import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userVersion, setUserVersion] = useState(0); // Version counter to force re-renders

  const clearAuth = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        clearAuth();
      }
    }
    setLoading(false);
  }, [clearAuth]);

  // Listen for user-updated events to refresh user data
  useEffect(() => {
    const handleUserUpdate = (event) => {
      const { userId: eventUserId, user: updatedUser } = event.detail || {};
      const currentUserId = user?.id || user?.userId;
      
      // Only process if the event is for the current user (or if userId matches)
      if (eventUserId && currentUserId && eventUserId !== String(currentUserId)) {
        return; // Event is for a different user, ignore it
      }
      
      console.log('📢 User update event received in AuthContext, refreshing user data...', { eventUserId, updatedUser });
      
      // If updatedUser is provided, use it directly (ProfileEdit already merges the data)
      if (updatedUser) {
        // Always create a new object reference to ensure React detects the change
        const newUser = { ...updatedUser };
        setUser(newUser);
        // Increment version to force context value update
        setUserVersion(prev => {
          const newVersion = prev + 1;
          console.log('✅ AuthContext user updated from event, new reference created, version:', newVersion);
          return newVersion;
        });
        
        // Try to save to localStorage, but handle quota errors gracefully
        try {
          // Only store essential user data to avoid quota issues
          const essentialUserData = {
            id: newUser.id,
            userId: newUser.userId,
            email: newUser.email,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            phone: newUser.phone,
            country: newUser.country,
            roles: newUser.roles,
            emailVerified: newUser.emailVerified,
            phoneVerified: newUser.phoneVerified,
            isNewUser: newUser.isNewUser,
          };
          localStorage.setItem('user', JSON.stringify(essentialUserData));
          console.log('✅ AuthContext user saved to localStorage from event');
        } catch (error) {
          if (error.name === 'QuotaExceededError') {
            console.warn('⚠️ localStorage quota exceeded, continuing without localStorage save');
            // State is updated in memory, which is enough for immediate UI updates
          } else {
            console.error('❌ Error saving user to localStorage:', error);
          }
        }
        
        // Force a re-render by updating state with a new object reference
        // This ensures all components using useAuth() will re-render
        return;
      }
      
      // If event has userId but no updatedUser, try to fetch from backend as fallback
      if (eventUserId && token && !updatedUser) {
        console.log('⚠️ User update event received without user data, attempting to fetch from backend...');
        // Note: ProfileEdit already provides updatedUser in the event, so this is just a fallback
        api.getProfile(eventUserId).then(userProfile => {
          const userData = userProfile?.data || userProfile;
          if (userData) {
            const currentUser = user || {};
            const mergedUser = { ...currentUser, ...userData };
            // Create new object reference
            const newUser = { ...mergedUser };
            setUser(newUser);
            localStorage.setItem('user', JSON.stringify(newUser));
            console.log('✅ AuthContext user refreshed from backend fallback, new reference created:', newUser);
          }
        }).catch(error => {
          console.warn('⚠️ Could not refresh user data from backend:', error);
        });
      }
    };

    window.addEventListener('user-updated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('user-updated', handleUserUpdate);
    };
  }, [token, user]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await api.login(email, password);
      // apiClient already extracts response.data.data, so response is the AuthResponse object
      const { token: authToken, ...userData } = response;
      
      // Normalize roles - convert Role enum to array if needed
      if (userData.roles && !Array.isArray(userData.roles)) {
        userData.roles = [userData.roles];
      }
      
      // Store token and user
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(authToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || error.response?.data?.message || 'Login failed',
        errorCode: error.errorCode || error.response?.data?.errorCode || error.data?.errorCode
      };
    }
  }, []);

  const register = useCallback(async (email, password, firstName, lastName, phone, acceptedTerms = false) => {
    try {
      const response = await api.register(email, password, firstName, lastName, phone, acceptedTerms);
      // apiClient already extracts the data, so response is the AuthResponse object directly
      const { token: authToken, ...userData } = response;
      
      // Normalize roles - convert Role enum to array if needed
      if (userData.roles && !Array.isArray(userData.roles)) {
        userData.roles = [userData.roles];
      }
      
      if (authToken) {
        // Store token and user for verified flows
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        setToken(authToken);
        setUser(userData);
        setIsAuthenticated(true);
      }
      
      return { success: true, user: userData, requiresEmailVerification: !authToken, isNewUser: response.isNewUser || true };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.message || error.response?.data?.message || 'Registration failed' 
      };
    }
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    // DON'T clear onboardingComplete on logout - user should stay in their state
    // The profile check on login will determine if they need onboarding
    // Redirect to landing page
    window.location.href = '/';
  }, [clearAuth]);

  const updateUser = useCallback((userData) => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔧 AuthContext.updateUser CALLED');
    console.log('   Current user state:', {
      id: user?.id,
      userId: user?.userId,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
    });
    console.log('   New userData received:', {
      id: userData?.id,
      userId: userData?.userId,
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      email: userData?.email,
    });
    console.log('   Current userVersion:', userVersion);
    
    // Always create a new object reference to ensure React detects the change
    const newUser = { ...userData };
    console.log('   Created newUser object:', {
      id: newUser.id,
      userId: newUser.userId,
      firstName: newUser.firstName,
      lastName: newUser.lastName,
    });
    console.log('   Are user and newUser different references?', user !== newUser);
    console.log('   Are firstNames different?', user?.firstName !== newUser.firstName);
    console.log('   Are lastNames different?', user?.lastName !== newUser.lastName);
    
    console.log('📝 Calling setUser with newUser...');
    setUser(newUser);
    console.log('✅ setUser called');
    
    // Increment version to force context value update
    setUserVersion(prev => {
      const newVersion = prev + 1;
      console.log('📝 Incrementing userVersion from', prev, 'to', newVersion);
      return newVersion;
    });
    console.log('═══════════════════════════════════════════════════════════');
    
    // Try to save to localStorage, but handle quota errors gracefully
    try {
      // Only store essential user data to avoid quota issues
      const essentialUserData = {
        id: newUser.id,
        userId: newUser.userId,
        email: newUser.email,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        phone: newUser.phone,
        country: newUser.country,
        roles: newUser.roles,
        emailVerified: newUser.emailVerified,
        phoneVerified: newUser.phoneVerified,
        isNewUser: newUser.isNewUser,
      };
      localStorage.setItem('user', JSON.stringify(essentialUserData));
      console.log('✅ AuthContext.updateUser saved to localStorage:', newUser);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ localStorage quota exceeded, attempting to clear old data...');
        try {
          // Try to clear old/session data
          sessionStorage.clear();
          // Try storing minimal data
          const minimalData = {
            id: newUser.id,
            userId: newUser.userId,
            email: newUser.email,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
          };
          localStorage.setItem('user', JSON.stringify(minimalData));
          console.log('✅ Saved minimal user data to localStorage');
        } catch (retryError) {
          console.error('❌ Failed to save user to localStorage:', retryError);
          // Continue without localStorage - state will still update in memory
          // User will need to log in again if they refresh, but app will work
        }
      } else {
        console.error('❌ Error saving user to localStorage:', error);
      }
    }
  }, [user, userVersion]);

  const verifyEmail = useCallback(async (token) => {
    try {
      await api.verifyEmail(token);
      if (user) {
        updateUser({ ...user, emailVerified: true });
      }
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Email verification failed';
      return { success: false, error: message };
    }
  }, [user, updateUser]);

  const verifyEmailWithCode = useCallback(async (email, code) => {
    try {
      await api.verifyEmailWithCode(email, code);
      if (user) {
        updateUser({ ...user, emailVerified: true });
      }
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Email verification failed';
      return { success: false, error: message };
    }
  }, [user, updateUser]);

  const verifyPhone = useCallback(async (phone, code) => {
    try {
      await api.verifyPhone(phone, code);
      if (user) {
        updateUser({ ...user, phoneVerified: true });
      }
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Phone verification failed' 
      };
    }
  }, [user, updateUser]);

  const oauthLogin = useCallback(async (provider, providerId, email, firstName, lastName) => {
    try {
      const response = await api.oauthLogin(provider, providerId, email, firstName, lastName);
      // apiClient already extracts response.data.data, so response is the AuthResponse object
      const { token: authToken, ...userData } = response;
      
      // Normalize roles - convert Role enum to array if needed
      if (userData.roles && !Array.isArray(userData.roles)) {
        userData.roles = [userData.roles];
      }
      
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(authToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData, isNewUser: response.isNewUser };
    } catch (error) {
      console.error('OAuth login error:', error);
      return { 
        success: false, 
        error: error.message || error.response?.data?.message || 'OAuth login failed',
        errorCode: error.errorCode || error.response?.data?.errorCode || error.data?.errorCode
      };
    }
  }, []);

  // Use useMemo to ensure value object is recreated when user or userVersion changes
  // This forces all components using useAuth() to re-render
  const value = useMemo(() => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔄 AuthContext VALUE RECREATED');
    console.log('   user:', {
      id: user?.id,
      userId: user?.userId,
      firstName: user?.firstName,
      lastName: user?.lastName,
    });
    console.log('   userVersion:', userVersion);
    console.log('   isAuthenticated:', isAuthenticated);
    console.log('   loading:', loading);
    console.log('═══════════════════════════════════════════════════════════');
    return {
      user,
      token,
      isAuthenticated,
      loading,
      login,
      register,
      logout,
      verifyEmail,
      verifyEmailWithCode,
      verifyPhone,
      oauthLogin,
      updateUser,
      setToken,
      setIsAuthenticated,
      _version: userVersion, // Include version in value to force updates
    };
  }, [user, token, isAuthenticated, loading, userVersion, login, register, logout, verifyEmail, verifyEmailWithCode, verifyPhone, oauthLogin, updateUser, setToken, setIsAuthenticated]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

